import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { array, bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import omit from 'lodash/omit';
import { propTypes, LISTING_STATE_CLOSED, LINE_ITEM_NIGHT, LINE_ITEM_DAY } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import config from '../../config';
import { ModalInMobile, Button } from '../../components';
import { BookingTimeForm } from '../../forms';

import css from './BookingPanel.module.css';
import { types as sdkTypes } from '../../util/sdkLoader';
import * as validators from '../../util/validators';

const { Money } = sdkTypes;

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openBookModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), book: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeBookModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'book');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    title,
    subTitle,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    options,
    publicData,
  } = props;
  const price = listing.attributes.price;
  const priceChild =
    listing.attributes.publicData && listing.attributes.publicData.priceChild
      ? listing.attributes.publicData.priceChild
      : null;
  const priceInfant = 
    listing.attributes.publicData && listing.attributes.publicData.priceInfant
      ? listing.attributes.publicData.priceInfant
      : null;
  const discount = typeof listing.attributes.publicData.discountPercentage !== 'undefined' ? listing.attributes.publicData.discountPercentage : 0;
  const discountedPrice = Object.create(price);
  discountedPrice.amount = (100 + discount) * .01 * discountedPrice.amount;

  const timeZone =
    listing.attributes.availabilityPlan && listing.attributes.availabilityPlan.timezone;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingTimeForm = hasListingState && !isClosed && !isOwnListing;
  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, intl);
  const priceDiscountedValue = priceData(new Money(((100 + discount) * .01 * price.amount), price.currency), intl);
  const priceChildValue = priceChild !== null ? (priceChild.amount !== null ? priceData(new Money(priceChild.amount, priceChild.currency), intl) : null) : null;
  const priceChildDiscountedValue = priceChild !== null ? (priceChild.amount !== null ? priceData(new Money(((100 + discount) * .01 * priceChild.amount), priceChild.currency), intl) : null) : null;
  const priceInfantValue = priceInfant !== null ? (priceInfant.amount !== null ? priceData(new Money(priceInfant.amount, priceInfant.currency), intl) : null) : null;
  const priceInfantDiscountedValue = priceInfant !== null ? (priceInfant.amount !== null ? priceData(new Money(((100 + discount) * .01 * priceInfant.amount), priceInfant.currency), intl) : null) : null;
  const isBook = !!parse(location.search).book;

  const subTitleText = !!subTitle
    ? subTitle
    : showClosedListingHelpText
    ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
    : null;

  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;
  const isGroupTour = listing.attributes.publicData && listing.attributes.publicData.groupTour ? true: false;
  const tourSize = listing.attributes.publicData && listing.attributes.publicData.groupTour ? listing.attributes.publicData.groupTourSize : null
  const unitTranslationKey = listing.attributes.publicData && listing.attributes.publicData.groupTour ? 'TransactionPanel.perGroup' 
    : (
      isNightly
      ? 'BookingPanel.perNight'
      : isDaily
      ? 'BookingPanel.perDay'
      : 'BookingPanel.perUnit'
    );
  ;

  const classes = clsx(rootClassName || css.root, className);
  const titleClasses = clsx(titleClassName || css.bookingTitle);

  const adultPriceDisplay = (
    <div>
      { discount < 0 ? (
        <span>
          <div className={css.originalPriceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={css.priceValue} title={priceTitle}>
            {priceDiscountedValue.formattedPrice}
          </div>
        </span>
      ) : (
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
      )}
      <div className={css.perUnit}>
        {isGroupTour ? 
          <FormattedMessage id={'BookingPanel.perGroup'} values={{tourSize : tourSize }} />
            : <FormattedMessage id={unitTranslationKey} />
        }
      </div>
    </div>
  )

  const childPriceDisplay = priceChildValue !== null ? (
    <div>
      { discount < 0 ? (
        <span>
          <div className={ css.originalPriceValue } title={priceChild}>
            {priceChildValue.formattedPrice}
          </div>
          <div className={ css.priceValue } title={priceChild}>
            {priceChildDiscountedValue.formattedPrice}
          </div>
        </span>
      ) : (
        <div className={ css.priceValue } title={priceChild}>
          {priceChildValue.formattedPrice}
        </div>
      )}
      <div className={ css.perUnit }>
        <FormattedMessage id={"BookingPanel.perChild"} />
      </div>
    </div>): null;
  
  const infantPriceDisplay = priceInfantValue !== null ? (
    <div>
      { discount < 0 ? (
        <span>
          <div className={ css.originalPriceValue } title={priceChild}>
            {priceInfantValue.formattedPrice}
          </div>
          <div className={ css.priceValue } title={priceChild}>
            {priceInfantDiscountedValue.formattedPrice}
          </div>
        </span>
      ) : (
        <div className={ css.priceValue } title={priceChild}>
          {priceInfantValue.formattedPrice}
        </div>
      )}
      <div className={ css.perUnit }>
        <FormattedMessage id={"BookingPanel.perInfant"} />
      </div>
    </div>): null;

  const groupValid = validators.groupTourSizeValidation(
    "max capacity",
    tourSize,
    isGroupTour
  )

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="BookingTimeFormInModal"
        isModalOpenOnMobile={isBook}
        onClose={() => closeBookModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalHeading}>
          <h2 className={css.title}>{title}</h2>
        </div>
        <div className={css.bookingHeading}>
          <div className={css.desktopPriceContainer}>
            <div>
              {adultPriceDisplay}
            </div>
            <div>
              {childPriceDisplay}
            </div>
            <div>
              {infantPriceDisplay}
            </div>
          </div>
          <div className={css.bookingHeadingContainer}>
            <h2 className={titleClasses}>{title}</h2>
            {subTitleText ? <div className={css.bookingHelp}>{subTitleText}</div> : null}
          </div>
        </div>

        {showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.submitButtonWrapper}
            unitType={unitType}
            onSubmit={onSubmit}
            price={price}
            priceChild={priceChild}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            options={options}
            publicData={publicData}
            validate={groupValid}
          />
        ) : (
          "You can't book this listing."
        )}
      </ModalInMobile>
      <div className={css.openBookingForm} id="openBookingForm">
        <div className={css.priceContainer}>
          <div>
            {adultPriceDisplay}
          </div>
          <div>
            {childPriceDisplay}
          </div>
          <div>
              {infantPriceDisplay}
          </div>
        </div>

        {showBookingTimeForm ? (
          <Button
            rootClassName={css.bookButton}
            onClick={() => openBookModal(isOwnListing, isClosed, history, location)}
          >
            <FormattedMessage id="BookingPanel.ctaButtonMessage" />
          </Button>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="BookingPanel.closedListingButtonText" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  subTitle: null,
  unitType: config.bookingUnitType,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.bookingUnitType,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),
  authorDisplayName: oneOfType([node, string]).isRequired,
  onManageDisableScrolling: func.isRequired,
  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(React.memo(BookingPanel));
